import { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Plus, FileText } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Badge } from '@/shared/components/ui/Badge';
import { Table, type Column } from '@/shared/components/ui/Table';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { ConfirmDialog } from '@/shared/components/ui/ConfirmDialog';
import { useUser } from '@/shared/hooks/useAuth';
import { useSalesDocs, useSalesDocMutations } from '../hooks/useSalesDocs';
import { SalesDocFormModal } from '../components/SalesDocFormModal';
import { SalesDocDrawer } from '../components/SalesDocDrawer';
import { detectFileType, deriveFileName } from '../constants';
import { getUploaderId } from '../types';
import type { CreateSalesDocPayload, SalesDoc, UpdateSalesDocPayload } from '../types';

function fmtDate(iso?: string) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function SalesDocsPage() {
  const user = useUser();
  const role = user?.role ?? '';
  const isPrivileged = ['admin', 'superadmin'].includes(role);

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const { data: docs = [], isLoading, isError } = useSalesDocs();
  const { createDoc, updateDoc, deleteDoc } = useSalesDocMutations();

  const [formOpen, setFormOpen] = useState(false);
  const [editDoc, setEditDoc] = useState<SalesDoc | null>(null);
  const [drawerDoc, setDrawerDoc] = useState<SalesDoc | null>(null);
  const [confirmDoc, setConfirmDoc] = useState<SalesDoc | null>(null);

  const canManage = (doc: SalesDoc) => isPrivileged || (!!user?.id && getUploaderId(doc.uploaded_by) === user.id);

  // Filter options derived from the loaded list (real/derived-real values only).
  const typeOptions = useMemo(() => {
    const set = new Set(docs.map((d) => detectFileType(d.url).label));
    return [{ value: '', label: 'All types' }, ...Array.from(set).sort().map((t) => ({ value: t, label: t }))];
  }, [docs]);

  const ownerOptions = useMemo(() => {
    const set = new Set(docs.map((d) => d.creator_name).filter(Boolean) as string[]);
    return [{ value: '', label: 'All owners' }, ...Array.from(set).sort().map((o) => ({ value: o, label: o }))];
  }, [docs]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return docs.filter((d) => {
      if (typeFilter && detectFileType(d.url).label !== typeFilter) return false;
      if (ownerFilter && d.creator_name !== ownerFilter) return false;
      if (!q) return true;
      return [d.title, d.description, d.creator_name, fmtDate(d.created_at), deriveFileName(d.url), detectFileType(d.url).label].some(
        (f) => (f ?? '').toLowerCase().includes(q),
      );
    });
  }, [docs, search, typeFilter, ownerFilter]);

  const handleCreate = () => {
    setEditDoc(null);
    setFormOpen(true);
  };
  const handleEdit = (doc: SalesDoc) => {
    setDrawerDoc(null);
    setEditDoc(doc);
    setFormOpen(true);
  };

  const onCreate = (payload: CreateSalesDocPayload) =>
    createDoc.mutate(payload, {
      onSuccess: () => {
        toast.success('Document added.');
        setFormOpen(false);
      },
      onError: () => toast.error('Failed to add document.'),
    });

  const onUpdate = (id: string, payload: UpdateSalesDocPayload) =>
    updateDoc.mutate(
      { id, payload },
      {
        onSuccess: () => {
          toast.success('Document updated.');
          setFormOpen(false);
          setEditDoc(null);
        },
        onError: () => toast.error('Failed to update document.'),
      },
    );

  const handleDelete = () => {
    if (!confirmDoc) return;
    deleteDoc.mutate(confirmDoc.id, {
      onSuccess: () => {
        toast.success('Document deleted.');
        setConfirmDoc(null);
        setDrawerDoc(null);
      },
      onError: () => toast.error('Failed to delete document.'),
    });
  };

  const columns: Column<SalesDoc>[] = [
    {
      key: 'title',
      header: 'Document',
      render: (d) => {
        const ft = detectFileType(d.url);
        const Icon = ft.icon;
        return (
          <span className="flex items-center gap-2">
            <Icon className="h-4 w-4 shrink-0 text-on-surface-variant" />
            <span className="font-medium text-on-surface">{d.title}</span>
          </span>
        );
      },
    },
    { key: 'type', header: 'Type', render: (d) => <Badge tone={detectFileType(d.url).tone}>{detectFileType(d.url).label}</Badge> },
    { key: 'creator_name', header: 'Uploaded By', render: (d) => d.creator_name || '—' },
    { key: 'created_at', header: 'Uploaded', render: (d) => fmtDate(d.created_at) },
  ];

  return (
    <div className="flex h-full flex-col gap-4 p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileText className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-display-sm font-semibold text-on-surface">Sales Documents</h1>
            <p className="text-body-sm text-on-surface-variant">{visible.length} document{visible.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
        <Button onClick={handleCreate} size="md"><Plus className="h-4 w-4" /> New Document</Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="w-72">
          <Input name="docs-search" placeholder="Search title, type, owner…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="w-44">
          <Select name="docs-type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} options={typeOptions} aria-label="Filter by type" />
        </div>
        <div className="w-48">
          <Select name="docs-owner" value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)} options={ownerOptions} aria-label="Filter by owner" />
        </div>
      </div>

      {isLoading ? (
        <div className="flex flex-1 items-center justify-center"><Spinner /></div>
      ) : isError ? (
        <EmptyState title="Failed to load documents" description="Check your connection or try refreshing." />
      ) : visible.length === 0 ? (
        <EmptyState
          title={search || typeFilter || ownerFilter ? 'No matching documents' : 'No documents yet'}
          description={search || typeFilter || ownerFilter ? 'Try different filters or search term.' : 'Add a document link or upload a file.'}
          action={!search && !typeFilter && !ownerFilter ? <Button onClick={handleCreate}>New Document</Button> : undefined}
        />
      ) : (
        <div className="flex-1 overflow-y-auto">
          <Table columns={columns} data={visible} rowKey={(d) => d.id} onRowClick={setDrawerDoc} empty="No documents found." />
        </div>
      )}

      <SalesDocFormModal
        open={formOpen}
        doc={editDoc}
        loading={createDoc.isPending || updateDoc.isPending}
        onClose={() => {
          setFormOpen(false);
          setEditDoc(null);
        }}
        onCreate={onCreate}
        onUpdate={onUpdate}
      />

      <SalesDocDrawer
        doc={drawerDoc}
        canManage={!!drawerDoc && canManage(drawerDoc)}
        deleting={deleteDoc.isPending}
        onClose={() => setDrawerDoc(null)}
        onEdit={handleEdit}
        onDelete={setConfirmDoc}
      />

      <ConfirmDialog
        open={!!confirmDoc}
        title="Delete document"
        message={`Delete "${confirmDoc?.title ?? ''}"? This cannot be undone.`}
        confirmLabel="Delete"
        danger
        loading={deleteDoc.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDoc(null)}
      />
    </div>
  );
}
